import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileActiveDropdown, setMobileActiveDropdown] = useState<string | null>(null);

  const location = useLocation();
  const navigate = useNavigate();

  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const useCasesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleContactClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const contactSection = document.querySelector('#contact-form-section');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const contactSection = document.querySelector('#contact-form-section');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleHistoryClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const historySection = document.querySelector('#history-section');
        if (historySection) {
          const headerHeight = 80; // Approximate header height
          const elementPosition = historySection.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const historySection = document.querySelector('#history-section');
      if (historySection) {
        const headerHeight = 80; // Approximate header height
        const elementPosition = historySection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleLeadershipClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const teamSection = document.querySelector('#team-section');
        if (teamSection) {
          const headerHeight = 80; // Approximate header height
          const elementPosition = teamSection.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const teamSection = document.querySelector('#team-section');
      if (teamSection) {
        const headerHeight = 80; // Approximate header height
        const elementPosition = teamSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleDropdownToggle = (item: string) => {
    setActiveDropdown(activeDropdown === item ? null : item);
  };

  const handleMobileDropdownToggle = (item: string) => {
    setMobileActiveDropdown(mobileActiveDropdown === item ? null : item);
  };

  const closeDropdown = () => {
    setActiveDropdown(null);
  };

  const handleDropdownItemClick = (item: any) => {
    if (item.title === 'Our History') {
      handleHistoryClick();
    } else if (item.title === 'Leadership Team') {
      handleLeadershipClick();
    } else {
      navigate(item.href);
    }
    closeDropdown();
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src="/3.avif"
              alt="Dawki Mobility Logo"
              className="h-32 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {/* Home */}
            <Link
              to="/"
              className={`transition-colors duration-300 ${location.pathname === '/'
                  ? 'text-[#CC0000] font-semibold'
                  : isScrolled ? 'text-gray-900 hover:text-[#CC0000]' : 'text-white hover:text-[#CC0000]'
                }`}
            >
              Home
            </Link>

            {/* Vehicles */}
            <div className="relative">
              <button
                onClick={() => handleDropdownToggle('Vehicles')}
                className={`flex items-center space-x-1 transition-colors duration-300 ${activeDropdown === 'Vehicles'
                    ? 'text-[#CC0000]'
                    : isScrolled ? 'text-gray-900 hover:text-[#CC0000]' : 'text-white hover:text-[#CC0000]'
                  }`}
              >
                <span>Vehicles</span>
                <ChevronDown size={16} className={`transition-transform duration-300 ${activeDropdown === 'Vehicles' ? 'rotate-180' : ''}`} />
              </button>

              {activeDropdown === 'Vehicles' && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-100 z-50">
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Our Vehicle Range</h3>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-1">Electric Three-Wheelers</h4>
                        <p className="text-sm text-gray-600 mb-3">Sustainable commercial vehicles for last mile mobility</p>
                        <div className="space-y-2">
                          <Link
                            to="/passenger-vehicles"
                            className="block p-3 rounded-md hover:bg-gray-50 transition-colors duration-200"
                            onClick={closeDropdown}
                          >
                            <div className="font-medium text-gray-900">Passenger</div>
                            <div className="text-sm text-gray-600">Comfortable electric three-wheelers for passenger transport</div>
                          </Link>
                          <Link
                            to="/loader-vehicles"
                            className="block p-3 rounded-md hover:bg-gray-50 transition-colors duration-200"
                            onClick={closeDropdown}
                          >
                            <div className="font-medium text-gray-900">Loader</div>
                            <div className="text-sm text-gray-600">Efficient cargo transport solutions for last-mile delivery</div>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* About Us */}
            <div className="relative">
              <button
                onClick={() => handleDropdownToggle('About Us')}
                className={`flex items-center space-x-1 transition-colors duration-300 ${activeDropdown === 'About Us'
                    ? 'text-[#CC0000]'
                    : isScrolled ? 'text-gray-900 hover:text-[#CC0000]' : 'text-white hover:text-[#CC0000]'
                  }`}
              >
                <span>About Us</span>
                <ChevronDown size={16} className={`transition-transform duration-300 ${activeDropdown === 'About Us' ? 'rotate-180' : ''}`} />
              </button>

              {activeDropdown === 'About Us' && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-100 z-50">
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Our Company</h3>
                    <div className="space-y-2">
                      <Link
                        to="/mission"
                        className="block p-3 rounded-md hover:bg-gray-50 transition-colors duration-200"
                        onClick={closeDropdown}
                      >
                        <div className="font-medium text-gray-900">Our Mission</div>
                        <div className="text-sm text-gray-600">Our vision and core values</div>
                      </Link>
                      <button
                        onClick={() => handleDropdownItemClick({ title: 'Our History' })}
                        className="w-full text-left p-3 rounded-md hover:bg-gray-50 transition-colors duration-200"
                      >
                        <div className="font-medium text-gray-900">Our History</div>
                        <div className="text-sm text-gray-600">Journey of innovation and excellence</div>
                      </button>
                      <button
                        onClick={() => handleDropdownItemClick({ title: 'Leadership Team' })}
                        className="w-full text-left p-3 rounded-md hover:bg-gray-50 transition-colors duration-200"
                      >
                        <div className="font-medium text-gray-900">Leadership Team</div>
                        <div className="text-sm text-gray-600">Meet our experienced leadership</div>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Contact */}
            <button
              onClick={handleContactClick}
              className={`transition-colors duration-300 ${isScrolled ? 'text-gray-900 hover:text-[#CC0000]' : 'text-white hover:text-[#CC0000]'
                }`}
            >
              Contact
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden p-2 transition-colors duration-300 ${isScrolled ? 'text-gray-900' : 'text-white'
              }`}
            aria-label="Toggle mobile menu"
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center">
              <span className={`block w-5 h-0.5 bg-current transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-1' : ''}`}></span>
              <span className={`block w-5 h-0.5 bg-current transition-all duration-300 mt-1 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`block w-5 h-0.5 bg-current transition-all duration-300 mt-1 ${isMenuOpen ? '-rotate-45 -translate-y-1' : ''}`}></span>
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden ${isMenuOpen ? 'block' : 'hidden'} ${isScrolled ? 'bg-white' : 'bg-gray-900'
          } rounded-lg mt-4 shadow-lg`}>
          <div className="p-6 space-y-4">
            {/* Mobile Home */}
            <Link
              to="/"
              className={`block transition-colors duration-300 ${location.pathname === '/'
                  ? 'text-[#CC0000] font-semibold'
                  : isScrolled ? 'text-gray-900 hover:text-[#CC0000]' : 'text-white hover:text-[#CC0000]'
                }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>

            {/* Mobile Vehicles */}
            <div>
              <button
                onClick={() => handleMobileDropdownToggle('Vehicles')}
                className={`flex items-center justify-between w-full transition-colors duration-300 ${isScrolled ? 'text-gray-900 hover:text-[#CC0000]' : 'text-white hover:text-[#CC0000]'
                  }`}
              >
                <span>Vehicles</span>
                <ChevronRight size={16} className={`transition-transform duration-300 ${mobileActiveDropdown === 'Vehicles' ? 'rotate-90' : ''}`} />
              </button>

              {mobileActiveDropdown === 'Vehicles' && (
                <div className="mt-4 ml-4 space-y-3">
                  <Link
                    to="/passenger-vehicles"
                    className={`block transition-colors duration-300 ${isScrolled ? 'text-gray-900 hover:text-[#CC0000]' : 'text-white hover:text-[#CC0000]'
                      }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Passenger
                  </Link>
                  <Link
                    to="/loader-vehicles"
                    className={`block transition-colors duration-300 ${isScrolled ? 'text-gray-900 hover:text-[#CC0000]' : 'text-white hover:text-[#CC0000]'
                      }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Loader
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile About Us */}
            <div>
              <button
                onClick={() => handleMobileDropdownToggle('About Us')}
                className={`flex items-center justify-between w-full transition-colors duration-300 ${isScrolled ? 'text-gray-900 hover:text-[#CC0000]' : 'text-white hover:text-[#CC0000]'
                  }`}
              >
                <span>About Us</span>
                <ChevronRight size={16} className={`transition-transform duration-300 ${mobileActiveDropdown === 'About Us' ? 'rotate-90' : ''}`} />
              </button>

              {mobileActiveDropdown === 'About Us' && (
                <div className="mt-4 ml-4 space-y-3">
                  <Link
                    to="/mission"
                    className={`block transition-colors duration-300 ${
                      isScrolled ? 'text-gray-900 hover:text-[#CC0000]' : 'text-white hover:text-[#CC0000]'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Our Mission
                  </Link>
                  <button
                    onClick={() => {
                      handleDropdownItemClick({ title: 'Our History' });
                      setIsMenuOpen(false);
                    }}
                    className={`block w-full text-left transition-colors duration-300 ${
                      isScrolled ? 'text-gray-900 hover:text-[#CC0000]' : 'text-white hover:text-[#CC0000]'
                    }`}
                  >
                    Our History
                  </button>
                  <button
                    onClick={() => {
                      handleDropdownItemClick({ title: 'Leadership Team' });
                      setIsMenuOpen(false);
                    }}
                    className={`block w-full text-left transition-colors duration-300 ${
                      isScrolled ? 'text-gray-900 hover:text-[#CC0000]' : 'text-white hover:text-[#CC0000]'
                    }`}
                  >
                    Leadership Team
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Contact */}
            <button
              onClick={() => {
                handleContactClick();
                setIsMenuOpen(false);
              }}
              className={`block w-full text-left transition-colors duration-300 ${isScrolled ? 'text-gray-900 hover:text-[#CC0000]' : 'text-white hover:text-[#CC0000]'
                }`}
            >
              Contact
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;